import React from 'react';
import type { PlatformUser } from '../../types/user';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Badge } from '../../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { Shield } from 'lucide-react';
import { UserActionsMenu } from './UserActionsMenu';

interface UserListProps {
  users: PlatformUser[];
  selectedUsers: string[];
  onSelectUser: (userId: string) => void;
  onSelectAll: () => void;
  onEditUser: (user: PlatformUser) => void;
  onDeleteUser: (userId: string) => void;
  onResetPassword: (userId: string) => void;
  onToggleStatus: (userId: string, isDisabled: boolean) => void;
  currentUser: PlatformUser;
}

export const UserList: React.FC<UserListProps> = ({
  users,
  selectedUsers,
  onSelectUser,
  onSelectAll,
  onEditUser,
  onDeleteUser,
  onResetPassword,
  onToggleStatus,
  currentUser
}) => {
  const allSelected = users.length > 0 && selectedUsers.length === users.length;

  const getStatusColor = (status: PlatformUser['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleBadgeColor = (role: PlatformUser['role']) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'user':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <Checkbox
                checked={allSelected}
                onCheckedChange={onSelectAll}
                aria-label="Select all users"
              />
            </TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Department</TableHead>
            <TableHead>Company</TableHead>
            <TableHead>2FA</TableHead>
            <TableHead>Last Login</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} className={selectedUsers.includes(user.id) ? 'bg-gray-50' : undefined}>
              <TableCell>
                <Checkbox
                  checked={selectedUsers.includes(user.id)}
                  onCheckedChange={() => onSelectUser(user.id)}
                  aria-label={`Select ${user.name}`}
                />
              </TableCell>
              <TableCell>
                <div className="flex items-center">
                  <div className="text-sm font-medium text-gray-900">{user.name}</div>
                  {user.requirePasswordChange && (
                    <Badge variant="outline" className="ml-2 text-yellow-600 border-yellow-600">
                      Password Change Required
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-gray-500">{user.email}</div>
              </TableCell>
              <TableCell>
                <Badge className={getRoleBadgeColor(user.role)}>
                  {user.role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge className={getStatusColor(user.status)}>
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="text-sm text-gray-500">{user.profile.department?.name || '-'}</div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-gray-500">{user.profile.company?.name || '-'}</div>
              </TableCell>
              <TableCell>
                <div className="flex items-center">
                  {user.twoFactorEnabled ? (
                    <Shield className="h-4 w-4 text-green-500" />
                  ) : (
                    <Shield className="h-4 w-4 text-gray-300" />
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-gray-500">
                  {new Date(user.lastLogin).toLocaleDateString()}
                </div>
              </TableCell>
              <TableCell >
                <div className="flex items-center">
                  <UserActionsMenu
                    user={user}
                    currentUser={currentUser}
                    onEdit={onEditUser}
                    onResetPassword={onResetPassword}
                    onToggleStatus={onToggleStatus}
                    onDelete={onDeleteUser}
                  />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}; 